// "Projekt B2 neu – Testbuch" (Karabatos-Verlag, Athen 2018, ISBN 978-960-465-082-8).
// Structure verified against the scanned PDF: 15 Modelltests, each with Lesen Teil 1–5 on the
// same page layout; item numbers and answer letters follow the book's "Antwortbogen LESEN".
// Passages are NOT retyped here (copyrighted); the UI shows the user's own scanned pages,
// rendered locally into /public/exam-sources/projekt-b2-neu (git-ignored).
// Answer keys come from the Lehrerbuch and live in answer-key.json; a question without a
// verified key is never auto-graded.
import answerKey from './answer-key.json';

export const SOURCE_BOOK = {
  id: 'projekt-b2-neu',
  title: 'Projekt B2 neu – Testbuch',
  publisher: 'Chr. Karabatos-Verlag, Athen',
  year: 2018,
  isbn: '978-960-465-082-8',
  file: 'Projekt-B2-neu-Testbuch1.pdf',
  answerKeySource: 'Projekt B2 neu – Lehrerbuch',
};

// Teil definitions (Goethe-Zertifikat B2, Modul Lesen) as printed on the Antwortbogen.
// textPages / questionPages split each Teil's scans into reading text and task sheet;
// `crop` is the [top, bottom] fraction of the page to show (Teil 4 has both on its first page).
// Teil 5 is one sheet where headings and paragraphs belong together, so it is all text.
const T4_SPLIT = 0.595;
const T4_FOOTER = 0.885;
export const LESEN_TEILE = [
  { teil: 1, type: 'matching', label: 'Forumsbeiträge – Aussagen zuordnen', first: 1, last: 9, options: ['a', 'b', 'c', 'd'], pageOffsets: [0, 1], textPages: [{ offset: 1 }], questionPages: [{ offset: 0 }], minutes: 18 },
  { teil: 2, type: 'gap', label: 'Lücken im Text – Sätze einfügen', first: 10, last: 15, options: ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'], pageOffsets: [2, 3], textPages: [{ offset: 2 }], questionPages: [{ offset: 3 }], minutes: 12 },
  { teil: 3, type: 'single', label: 'Artikel – Multiple Choice', first: 16, last: 21, options: ['a', 'b', 'c'], pageOffsets: [4, 5], textPages: [{ offset: 4 }], questionPages: [{ offset: 5 }], minutes: 12 },
  { teil: 4, type: 'matching', label: 'Meinungsäußerungen – Überschriften zuordnen', first: 22, last: 27, options: ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'], pageOffsets: [6, 7], textPages: [{ offset: 6, crop: [T4_SPLIT, T4_FOOTER] }, { offset: 7 }], questionPages: [{ offset: 6, crop: [0, T4_SPLIT] }], minutes: 12 },
  { teil: 5, type: 'headings', label: 'Abschnitte – Überschriften auswählen', first: 28, last: 30, options: ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'], pageOffsets: [8], textPages: [{ offset: 8 }], questionPages: [], minutes: 6 },
];

const TEST_COUNT = 15;
const FIRST_PDF_PAGE = 7; // Modelltest 1, Lesen Teil 1 (book page 6)
const PAGES_PER_TEST = 16;

const keyFor = (test, teil) => answerKey.tests?.[String(test)]?.[String(teil)] ?? null;

export const MODELLTESTS = Array.from({ length: TEST_COUNT }, (_, i) => {
  const test = i + 1;
  const base = FIRST_PDF_PAGE + PAGES_PER_TEST * i;
  return {
    id: `projekt-b2-neu-mt${test}`,
    number: test,
    title: `Modelltest ${test}`,
    teile: LESEN_TEILE.map((t) => {
      const key = keyFor(test, t.teil);
      const questions = Array.from({ length: t.last - t.first + 1 }, (_, q) => {
        const number = t.first + q;
        const correct = key?.[String(number)] ?? null;
        return { id: `mt${test}-l${t.teil}-q${number}`, number, options: t.options, correct, verified: Boolean(correct) };
      });
      return {
        ...t,
        id: `projekt-b2-neu-mt${test}-lesen-${t.teil}`,
        test,
        module: 'lesen',
        pdfPages: t.pageOffsets.map((o) => base + o),
        textPages: t.textPages.map((p) => ({ page: base + p.offset, crop: p.crop })),
        questionPages: t.questionPages.map((p) => ({ page: base + p.offset, crop: p.crop })),
        bookPages: t.pageOffsets.map((o) => base + o - 1),
        questions,
        gradable: questions.every((q) => q.verified),
      };
    }),
  };
});

export const getTeil = (test, teil) =>
  MODELLTESTS.find((m) => m.number === Number(test))?.teile.find((t) => t.teil === Number(teil)) ?? null;

export const pageImageUrl = (pdfPage) =>
  `${process.env.PUBLIC_URL || ''}/exam-sources/projekt-b2-neu/p${String(pdfPage).padStart(3, '0')}.jpg`;

// Grade only verified questions; unanswered never counts as correct.
export function gradeTeil(teil, answers) {
  let correct = 0; let incorrect = 0; let unanswered = 0; let unverified = 0;
  const rows = teil.questions.map((q) => {
    const given = answers[q.id] ?? null;
    if (!q.verified) { unverified += 1; return { ...q, given, status: 'unverified' }; }
    if (!given) { unanswered += 1; return { ...q, given, status: 'unanswered' }; }
    if (given === q.correct) { correct += 1; return { ...q, given, status: 'correct' }; }
    incorrect += 1; return { ...q, given, status: 'incorrect' };
  });
  const gradedTotal = teil.questions.length - unverified;
  return { rows, correct, incorrect, unanswered, unverified, total: teil.questions.length, gradedTotal, score: gradedTotal ? Math.round((correct / gradedTotal) * 100) : null };
}
